"use client";

import { useCallback } from "react";
import type { FieldErrors } from "react-hook-form";
import {
  PAYMENT_PROVIDERS,
  PaymentProvider,
} from "@cappelendamm/e-commerce-services";
import { useTrackEvent } from "@/tracking/TrackingContext";
import type { EcommerceFunnel } from "@/tracking/e-commerce/funnel";
import type { CheckoutForm } from "./types";
import { getInvalidFieldPaths } from "./invalid-field-paths";

function toPaymentType(paymentType?: PaymentProvider) {
  if (!paymentType) return undefined;
  if (paymentType === PAYMENT_PROVIDERS.VIPPS) return "Vipps";
  return paymentType === PAYMENT_PROVIDERS.NETS ? "Kort" : "Faktura";
}

/**
 * Tracks what happens when the customer presses "pay" in checkout: the
 * attempt, client-side validation failures and server-side rejections.
 * Together with `begin_checkout` and `purchase` these show where the
 * checkout loses people.
 */
export function useTrackCheckoutSubmit(funnel: EcommerceFunnel) {
  const trackEvent = useTrackEvent();

  const trackSubmitted = useCallback(
    (paymentType?: PaymentProvider) =>
      trackEvent({
        eventName: "checkout_submitted",
        funnel,
        payment_type: toPaymentType(paymentType),
      }),
    [funnel, trackEvent],
  );

  const trackValidationFailed = useCallback(
    (errors: FieldErrors<CheckoutForm>) => {
      const invalidFields = getInvalidFieldPaths(errors);
      trackEvent({
        eventName: "checkout_validation_failed",
        funnel,
        invalid_fields: invalidFields,
        invalid_field_count: invalidFields.length,
      });
    },
    [funnel, trackEvent],
  );

  const trackSubmitFailed = useCallback(
    (errorStatus: string, paymentType?: PaymentProvider) =>
      trackEvent({
        eventName: "checkout_submit_failed",
        funnel,
        error_status: errorStatus,
        payment_type: toPaymentType(paymentType),
      }),
    [funnel, trackEvent],
  );

  return { trackSubmitted, trackValidationFailed, trackSubmitFailed };
}
